"use client";

import type { ReactNode } from "react";
import Tooltip from "@ishvaram/ui/Tooltip";
import { JYOTISH_GLOSSARY, type GlossaryKey } from "@/lib/jyotish-glossary";

interface TermProps {
  /** Glossary key — see `src/lib/jyotish-glossary.ts` for the canonical list. */
  k: GlossaryKey | (string & {});
  /** Trigger text — usually the term as it reads in prose. */
  children: ReactNode;
}

/**
 * Inline glossary trigger for Jyotish technical terms.
 *
 * Wraps `<Tooltip>` from `@ishvaram/ui` with the matching entry from
 * `JYOTISH_GLOSSARY`. Falls back to plain text if the key is missing —
 * with a `console.warn` in development so we catch typos early.
 *
 * Usage:
 *   <Term k="dosha">dosha</Term>
 *   <Term k="kaal sarp dosh">Kaal Sarp Dosha</Term>
 */
export default function Term({ k, children }: TermProps) {
  const entry = (JYOTISH_GLOSSARY as Record<string, { title: string; text: string }>)[k];

  if (!entry) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[Term] Missing glossary key: "${k}". Falling back to plain text.`);
    }
    return <>{children}</>;
  }

  return (
    <Tooltip title={entry.title} text={entry.text} triggerStyle="plain">
      {children}
    </Tooltip>
  );
}
